import MomentUtil from './MomentUtil'
import { backendDateTimeFormat } from './MomentUtil'
const requiredRule = value => !!value || 'Required.'
const fieldTypes = [
  { name: 'String', rules: [requiredRule] },
  {
    name: 'Number',
    rules: [
      requiredRule,
      value => {
        const pattern = /^[0-9]+\.?[0-9]*$/
        return pattern.test(value) || 'Please enter a valid number.'
      },
    ],
  },
  { name: 'DateTime', rules: [requiredRule] },
  { name: 'MonitoringLocation', rules: [value => value.length > 0 || 'Required.'], isGroupedField: true },
  { name: 'MonitoringComponent', rules: [value => value.length > 0 || 'Required.'], isGroupedField: true },
  {
    name: 'Guid',
    rules: [requiredRule, value => {
      const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      return pattern.test(value) || 'Please enter a valid global unique identifier.'
    } ],
  },
]
export default {
  mixins: [MomentUtil],
  computed: {
    fieldTypeNames() {
      return fieldTypes.map(type => type.name)
    },
  },
  methods: {
    parseTypeFromString(type, value) {
      let parsedValue
      if (type == null) {
        return value
      }
      switch (type.toLowerCase()) {
      case 'string': {
        parsedValue = value
        break
      }
      case 'number': {
        let num = Number.parseFloat(value, 10)
        parsedValue = isNaN(num) ? value : num
        break
      }
      case 'datetime': {
        let parsedMoment = this.parseAmbiguousTextToMoment(value, { outputFormat: backendDateTimeFormat, utcOffsetMinutes: 0 })
        parsedValue = parsedMoment.isValid ? parsedMoment.formattedText : value
        break
      }
      case 'monitoringlocation': {
        try {
          if (typeof value == 'string' && value != '') {
            parsedValue = JSON.parse(value)
          } else {
            parsedValue = value
          }
        } finally {
          parsedValue = this.parseArrayValueHelper(parsedValue)
        }
        break
      }
      case 'monitoringcomponent': {
        try {
          if (typeof value == 'string' && value != '') {
            parsedValue = JSON.parse(value)
          } else {
            parsedValue = value
          }
        } finally {
          parsedValue = this.parseArrayValueHelper(parsedValue)
        }
        break
      }
      case 'guid': {
        parsedValue = value
        break
      }
      default: {
        console.error(`unknown type: ${type}`)
        parsedValue = value
      }
      }
      return parsedValue
    },
    encodeTypeToString(type, value) {
      let encodedValue
      switch (type.toLowerCase()) {
      case 'string': {
        encodedValue = value
        break
      }
      case 'number': {
        encodedValue = `${value}`
        break
      }
      case 'datetime': {
        let parsedMoment = this.parseAmbiguousTextToMoment(value, { isUTCString: true})
          //encodedValue = parsedMoment
        encodedValue = parsedMoment.parsedMoment.utc().format(backendDateTimeFormat)
        break
      }
      case 'monitoringlocation': {
        encodedValue = JSON.stringify(value)
        break
      }
      case 'monitoringcomponent': {
        encodedValue = JSON.stringify(value)
        break
      }
      case 'guid': {
        encodedValue = value
        break
      }
      default: {
        console.error(`unknown type: ${type}`)
        encodedValue = `${value}`
      }
      }
      return encodedValue
    },
    typeEntry(type) {
      return fieldTypes.find(x => type.toLowerCase() === x.name.toLowerCase())
    },
    inputRules(type) {
      let entry = this.typeEntry(type)
      return (entry == null || entry.rules == null) ? [] : entry.rules
    },
    isNonNullMetadataField(field) {
      if (field == null) {
        return false
      }
      if (Array.isArray(field.FieldValue)) {
        return (
          !!field.Field &&
          !!field.FieldType &&
          field.FieldValue.length > 0
        )
      } else{
        return (
          !!field.Field &&
          !!field.FieldType &&
          !!field.FieldValue
        )
      }
    },
    isNonInvalidDateField(field) {
      if (field.FieldType.toLowerCase() == 'datetime') {
        return (
          !!field.Field &&
          !!field.FieldType &&
          field.FieldValue != 'Invalid date'
        )
      } else {
        return true
      }
    },
    /**
     * Given an array with non-group metadata fields and group metadata fields,
     * collapse all group metadata fields that have the same FieldName into one field.
     * The collapsed field will have a FieldValue of Array[String]
     *
     * @param {Array[Object]} fields
     */
    collapseGroupedFields(fields) {
      let that = this
      let copiedFields = JSON.parse(JSON.stringify(fields))
      let ungroupedFields = copiedFields.filter(x => !that.isGroupedFieldType(x.FieldType))
      let groupedFields = copiedFields.filter(x => that.isGroupedFieldType(x.FieldType))
      let collapsedGroupedFields = this.flattenNestedFields(this.groupFieldsByNameAndType(groupedFields))
      let result = ungroupedFields.concat(collapsedGroupedFields)
      result.sort(that.compareFields)
      return result
    },

    /**
     * Given an array with non-group metadata fields and collapsed group metadata fields,
     * returns an array with non-group metadata fields and group metadata fields that are expanded
     * such that each group metadata field will have FieldValue of String
     * @param {Array[Object]} fields
     */
    expandGroupedFields(fields) {
      let result = []
      fields.forEach(fOriginal => {
        let f = JSON.parse(JSON.stringify(fOriginal))
        if (Array.isArray(f.FieldValue)) {
          f.FieldValue.forEach(v => {
            result.push(Object.assign({}, f, {
              FieldValue: v,
            }))
          })
          if (f.FieldValue.length == 0) {
            result.push(Object.assign({}, f, {
              FieldValue: '',
            }))
          }
        } else {
          result.push(f)
        }
      })
      return result
    },
    /**
     * Helper function to create collapsed metadata fields
     * @param {Array[Object]} fields - File Metadata Fields
     */
    groupFieldsByNameAndType(fields) {
      let that = this
      let groupedFields = {}
      fields.forEach(f => {
        let outerKey = f.Field
        let innerKey = f.FieldType
        if (!Object.keys(groupedFields).includes(outerKey)) {
          groupedFields[outerKey] = {}
        }
        if (!Object.keys(groupedFields[outerKey]).includes(innerKey)) {
          groupedFields[outerKey][innerKey] = Object.assign({}, f, { FieldValue: [] })
        }
        groupedFields[outerKey][innerKey] = that.groupFieldsByNameAndTypeHelper(
          groupedFields[outerKey][innerKey],
          f
        )
      })
      return groupedFields
    },
    groupFieldsByNameAndTypeHelper(obj, field) {
      obj.FieldValue.push(field.FieldValue)
      obj.Id = this.lowerNonNullValue(field.Id, obj.Id)
      obj.Ordinal = this.lowerNonNullValue(field.Ordinal, obj.Ordinal)
      return obj
    },
    lowerNonNullValue(a, b) {
      if (a == null) return b
      if (b == null) return b
      return (a < b) ? a : b
    },
    /**
     * Helper function to flatten FieldName FieldType dictionary
     * @param {Dict[Dict[Object]]} groupedFields - Nested dictionary
     */
    flattenNestedFields(groupedFields) {
      let fields = []
      Object.keys(groupedFields).forEach(outerKey => {
        Object.keys(groupedFields[outerKey]).forEach(innerKey => {
          fields.push(JSON.parse(JSON.stringify(groupedFields[outerKey][innerKey])))
        })
      })

      return fields
    },
    /**
     * Returns boolean indicating whether or not the field type is a grouped type.
     */
    isGroupedFieldType(type) {
      let typeEntry = this.typeEntry(type)
      return (typeEntry && typeEntry.isGroupedField) ? typeEntry.isGroupedField : false
    },
    compareFields(f1, f2) {
      let f1OrdinalIsNull = f1.Ordinal == null
      let f2OrdinalIsNull = f2.Ordinal == null
      if (!f1OrdinalIsNull && !f2OrdinalIsNull) return f1.Ordinal > f2.Ordinal ? 1 : -1
      if (f1OrdinalIsNull && !f2OrdinalIsNull) return 1
      if (!f1OrdinalIsNull && f2OrdinalIsNull) return -1
      if (f1OrdinalIsNull && f2OrdinalIsNull) {
        if (f1.Id != f2.Id) {
          return (f1.Id > f2.Id) ? 1 : -1
        } else {
          if (f1.Field != f2.Field) {
            return (f1.Field > f2.Field) ? 1 : -1
          } else {
            if (f1.FieldType != f2.FieldType) {
              return (f1.FieldType > f2.FieldType) ? 1 : -1
            } else {
              return (f1.FieldValue > f2.FieldValue) ? 1 : -1
            }
          }
        }
      }
    },
    parseArrayValueHelper(arrayValue) {
      let result = arrayValue
      if (!Array.isArray(arrayValue)) {
        result = arrayValue && arrayValue != '' ? [arrayValue] : []
      }
      return result
    },
    filterInvalidFieldValue(field) {
      switch(field.FieldType){
      case 'MonitoringLocation':
      case 'MonitoringComponent':
        return field.FieldValue.filter(x => x.trim() != '')
      default:
        return field.FieldValue
      }
    },
  },
}